/* eslint-disable react/display-name, react/jsx-one-expression-per-line */
import React, { memo, useMemo } from 'react';
import { Box, Text, Button, Circle, HStack } from '@chakra-ui/core';
import { MdVideocam, MdPhone, MdRecordVoiceOver } from 'react-icons/md';
import { IconType } from 'react-icons';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import { TFunction, useTranslation } from 'react-i18next';
import {
  AppointmentType,
  AvailableBookingWithPosition,
} from '../../../../../../firebase/firestore/documents/appointmentAvaiability';
import { useNextUpcomingBooking } from '../hooks/UseNextUpcomingBooking';
import { useStoreActions, useStoreState } from '../../../../../../models/hooks';
import { useInterviewVisibility } from '../hooks/useInterviewVisibility';
import { useInterviewDisplayTexts } from '../hooks/useInterviewDisplayTexts';
import { INTERVIEW_MESSAGING_TABS } from '../../../../../../routes/constants';
import { SeekerContainerTabItems } from '../../../SeekerContainerContext';
import { SESSION_KEY_CANDIDATE_LIST_TYPE } from '../../../../sidebar/filters/SeekersFiltersContext';
import { CandidateListType } from '../../../../sidebar/filters/seekersFiltersContextValueType';
import useCandidateBooking from '../../../../../../app/components/useCandidateBooking';
import { useMessagingViewContext } from '../../../../MessagingViewContext';
import { Candidate, EngagedCandidate } from '../../../../../../firebase/firestore/documents/candidate';

interface AppointmentTypeConfig {
  type: string;
  label: string;
  icon: IconType;
  circleIcon: boolean;
  ctaLabel: string | null;
}

const APPOINTMENT_TYPE_CONFIG: (t: TFunction<'messaging'>) => Record<AppointmentType, AppointmentTypeConfig> = (t) => ({
  [AppointmentType.video]: {
    type: 'video',
    label: t('upcommingInterviewBanner.videoInterview'),
    icon: MdVideocam,
    circleIcon: true,
    ctaLabel: t('upcommingInterviewBanner.buttonLabel'),
  },
  [AppointmentType.phone]: {
    type: 'phone',
    label: t('upcommingInterviewBanner.phoneInterview'),
    icon: MdPhone,
    circleIcon: false,
    ctaLabel: null,
  },
  [AppointmentType.inPerson]: {
    type: 'inPerson',
    label: t('upcommingInterviewBanner.inPersonInterview'),
    icon: MdRecordVoiceOver,
    circleIcon: false,
    ctaLabel: null,
  },
});

const getPrimaryType = (type?: AppointmentType | AppointmentType[]): AppointmentType => {
  if (!type) return AppointmentType.video;
  return Array.isArray(type) ? type[0] : type;
};

const extractDate = (raw?: string | Date | null): Date | null => {
  if (!raw) return null;
  const parsedDate = moment(raw);
  return parsedDate.isValid() ? parsedDate.toDate() : null;
};

const AppointmentIcon = memo(({ config, isSameUser }: { config: AppointmentTypeConfig; isSameUser: boolean }) => {
  const { icon: Icon, circleIcon } = config;
  const color = isSameUser ? 'orange.500' : 'blue.500';

  if (circleIcon) {
    return (
      <Circle size="24px" bg={color} flexShrink={0}>
        <Icon color="white" size={14} />
      </Circle>
    );
  }

  return (
    <Box color={color} flexShrink={0} lineHeight={0}>
      <Icon size={24} />
    </Box>
  );
});

const JoinButton = memo(
  ({
    candidate,
    config,
    isSameUser,
    isDisabled,
  }: {
    candidate: Candidate;
    config: AppointmentTypeConfig;
    isSameUser: boolean;
    isDisabled: boolean;
  }) => {
    const history = useHistory();
    const Icon = config.icon;

    const { id: candidateId } = candidate;

    const setInterviewEvent = useStoreActions((state) => state.interview.setInterviewEvent);
    const candidateAndBookingDetails = useCandidateBooking(candidateId);
    const { setSelectedEngagedCandidate, setSelectedEngagedSeeker } = useMessagingViewContext();

    const onJoinInterview = () => {
      setInterviewEvent(candidateAndBookingDetails as AvailableBookingWithPosition);
      setSelectedEngagedCandidate(candidate as EngagedCandidate);
      setSelectedEngagedSeeker(candidate && { seekerId: candidate.seekerId, candidateId: candidate.id });

      sessionStorage.setItem(SESSION_KEY_CANDIDATE_LIST_TYPE, CandidateListType.UNKNOWN);
      history.push(INTERVIEW_MESSAGING_TABS(candidateId, SeekerContainerTabItems.Messaging), {
        redirectedFromPage: 'messaging',
      });
    };

    return (
      <Button
        leftIcon={<Icon size={16} />}
        colorScheme="orange"
        variant={isSameUser ? 'solid' : 'outline'}
        size="sm"
        borderRadius="md"
        fontWeight="semibold"
        flexShrink={0}
        onClick={onJoinInterview}
        isDisabled={isDisabled}
      >
        {config.ctaLabel}
      </Button>
    );
  },
);

interface UpcomingBookingBannerProps {
  seekerId: string;
}

const UpcomingBookingBannerBase = ({ seekerId }: UpcomingBookingBannerProps) => {
  const { t } = useTranslation('messaging');

  const accountId = useStoreState((state) => state.app.user?.account);
  const userId = useStoreState((state) => state.app.user?.id);
  const nextBooking = useNextUpcomingBooking({ seekerId, accountId, userId });
  const location = useLocation();
  const isInterviewPage = location.pathname.startsWith('/interview');

  const fromDate = useMemo(() => extractDate(nextBooking?.appointment?.fromDate), [nextBooking]);
  const toDate = useMemo(() => extractDate(nextBooking?.appointment?.toDate), [nextBooking]);
  const isSameUser = useMemo(
    () => userId === nextBooking?.recruiterId,
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [userId, nextBooking?.recruiterId],
  );

  const { showButton, showBanner } = useInterviewVisibility(fromDate, toDate);

  const type = nextBooking?.appointment?.type;
  const config = APPOINTMENT_TYPE_CONFIG(t)[getPrimaryType(type)];

  const { title, subtitle, recruiterSuffix } = useInterviewDisplayTexts({
    fromDate,
    label: config?.label || /* istanbul ignore next */ t('upcommingInterviewBanner.videoInterview'),
    recruiterName: nextBooking?.appointment?.recruiterName,
    isSameUser,
  });

  if (!nextBooking || !showBanner || !fromDate) return null;

  const color = isSameUser ? 'orange.500' : 'blue.500';
  const bgColor = isSameUser ? 'orange.50' : 'blue.50';

  return (
    <HStack
      align="center"
      justify="space-between"
      bg={bgColor}
      borderBottom="2px solid"
      borderColor={color}
      px={6}
      py={3}
      spacing={3}
      flexWrap="wrap"
      width="100%"
    >
      <HStack align="center" flex="1" spacing={3} flexWrap="wrap" justify={config.type !== 'video' ? 'center' : 'flex-start'}>
        <AppointmentIcon config={config} isSameUser={isSameUser} />

        <Text fontWeight="bold" fontSize="sm" color="gray.800" whiteSpace="nowrap">
          {title}{' '}
          <Text as="span" color={color}>
            {recruiterSuffix}
          </Text>
        </Text>

        <Box as="span" w="6px" h="6px" borderRadius="full" bg="rgba(168, 177, 227, 1)" flexShrink={0} />

        <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
          {subtitle}
        </Text>
      </HStack>

      {!isInterviewPage && config.ctaLabel && (
        <JoinButton config={config} candidate={nextBooking.candidate} isSameUser={isSameUser} isDisabled={!showButton} />
      )}
    </HStack>
  );
};

export const UpcomingBookingBanner = memo(UpcomingBookingBannerBase);
